import { Component } from '@angular/core';
import { InvestorTab } from '@core/models/types';
import { MonthlyCalendarService } from '@core/services/monthly-calendar.service';
import * as moment from 'moment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { RouterService } from 'src/app/services/router/router.service';

interface CalendarEvent {
  title: string;
  startDate: string;
  endDate: string;
  allDay: boolean;
  userId: number;
  profilePictureUrl?: string;
}

@Component({
  selector: 'bpc-monthly-calendar',
  templateUrl: './monthly-calendar.component.html',
  styleUrls: ['./monthly-calendar.component.scss']  // Note: styleUrls should be plural.
})
export class MonthlyCalendarComponent {
  currentMonth = moment();
  // daysInMonth will now contain (moment | null) so that we can represent empty cells.
  daysInMonth: (moment.Moment | null)[] = [];
  weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  events: CalendarEvent[] = [];

  constructor(
    private monthlyCalendarService: MonthlyCalendarService,
    private routerService: RouterService,
  ) {}

  ngOnInit() {
    this.routerService.updateCurrentTab(InvestorTab.TRAVEL);
    this.generateCalendar();

    this.monthlyCalendarService.fetchMonthlyCalendarData().subscribe((response) => {
      if (response && response.data) {
        this.events = response.data.map((event: any) => ({
          title: `${event.user.name} (${event.location})`, // Show user name and location
          profilePictureUrl: event.user.profilePictureUrl,
          startDate: event.startDate,
          endDate: event.endDate,
          allDay: true,
          userId: event.userId // Store user ID for color mapping
        }));

        this.generateUserColors();
      }
    });
  }

  userColors: { [key: number]: string } = {}; // Store colors for user IDs

  generateUserColors() {
    const colors = [
      "#1F77B4", // Soft Blue
      "#FF7F0E", // Muted Orange
      "#2CA02C", // Gentle Green
      "#D62728", // Deep Red
      "#9467BD", // Elegant Purple
      "#8C564B", // Warm Brown
      "#E377C2", // Soft Pink
      "#7F7F7F", // Neutral Gray
      "#BCBD22", // Olive Green
      "#17BECF"  // Teal Blue
    ];
    let index = 0;

    this.events.forEach(event => {
      if (!this.userColors[event.userId]) {
        this.userColors[event.userId] = colors[index % colors.length];
        index++;
      }
    });
  }

  generateCalendar() {
    this.daysInMonth = [];

    const startOfMonth = this.currentMonth.clone().startOf("month");
    const endOfMonth = this.currentMonth.clone().endOf("month");

    // 1. Pad the beginning of the calendar with null values for days before the month starts.
    const startDayIndex = startOfMonth.day(); // Sunday = 0, Monday = 1, etc.
    for (let i = 0; i < startDayIndex; i++) {
      this.daysInMonth.push(null);
    }

    // 2. Add each day of the current month.
    for (
      let day = startOfMonth.clone();
      day.isBefore(endOfMonth, "day") || day.isSame(endOfMonth, "day");
      day.add(1, "day")
    ) {
      this.daysInMonth.push(day.clone());
    }

    // 3. Optionally, pad the end of the calendar to complete the final week.
    const totalCells = this.daysInMonth.length;
    const remainder = totalCells % 7;
    if (remainder !== 0) {
      const cellsToAdd = 7 - remainder;
      for (let i = 0; i < cellsToAdd; i++) {
        this.daysInMonth.push(null);
      }
    }
  }

  prevMonth() {
    this.currentMonth = this.currentMonth.subtract(1, "month");
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonth = this.currentMonth.add(1, "month");
    this.generateCalendar();
  }

  isToday(day: moment.Moment): boolean {
    return day.isSame(moment(), "day");
  }

  getEvents(day: moment.Moment): CalendarEvent[] {
    return this.events.filter((event) => 
      day.isBetween(event.startDate, event.endDate, "day", "[]")
    );
  }

  isStartOfEvent(day: moment.Moment, event: CalendarEvent): boolean {
    return day.isSame(moment(event.startDate), "day");
  }

  isEndOfEvent(day: moment.Moment, event: CalendarEvent): boolean {
    return day.isSame(moment(event.endDate), "day");
  }
}
